import { useEffect, useMemo } from "react";
import { useSnackbar } from "notistack";
import DataTable from "../DataTable.jsx";
import { useUser } from "../../hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import PropTypes from "prop-types";
import { useInsurerActions } from "../../hooks/useInsurerActions.jsx";
import { TableEntityCell } from "../../theme/components/index.js";

export default function InsurersTable({
  onRowClick = () => {},
  pageTitle,
  pageSubtitle,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const { state: insurersState, setState: setInsurersState } =
    useInsurerActions();

  const tableKey = "insurers";
  const storageKey = "v2_table_insurers";
  const defaultRowsPerPage = getUserRowsPerPage(user, 10);

  const rows = insurersState?.data?.data || [];

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Versicherungsgesellschaften",
        route: `/data/insurers`,
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey: storageKey,
        extraParams: {
          sort: [{ field: "name", order: "asc" }],
        },
        onStateChange: setInsurersState,
      },
    ],
    [defaultRowsPerPage, storageKey]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};

  useEffect(() => {
    fetchData(tableKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      size: 250,
      Cell: ({ row }) => {
        const name = row.original?.name || "—";
        return <TableEntityCell title={name} avatar={{ label: name }} />;
      },
    },
    {
      accessorKey: "id_ov",
      header: "ID OV",
      size: 100,
    },
    {
      accessorKey: "agenturnummer",
      header: "Agenturnummer",
      size: 150,
    },
    {
      accessorFn: (row) => row.ansprechpartner_ge?.email || "-",
      accessorKey: "ansprechpartner_email",
      header: "E-Mail-Adresse",
      size: 250,
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={rows}
      pageTitle={pageTitle}
      pageSubtitle={pageSubtitle}
      initialState={{ showGlobalFilter: true }}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      pageIndex={tableState.pageIndex}
      pageSize={tableState.pageSize}
      totalRows={tableState.totalRows}
      onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
      onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
      onChangePage={(nextOrUpdater) => {
        const base = {
          pageIndex: tableState.pageIndex ?? 0,
          pageSize: tableState.pageSize ?? defaultRowsPerPage,
        };
        const next =
          typeof nextOrUpdater === "function"
            ? nextOrUpdater(base)
            : nextOrUpdater;
        if (!next) {
          return;
        }
        updateTableState(tableKey, {
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        });
      }}
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      onRowClick={onRowClick}
      storageKey={storageKey}
      enableHeader
      enableSorting
      enableGlobalFilter={true}
      enableColumnFilters={true}
      enableColumnHiding={true}
      topToolbarIcons={true}
      enableTopToolbar={true}
    />
  );
}

InsurersTable.propTypes = {
  onRowClick: PropTypes.func,
  pageTitle: PropTypes.string,
  pageSubtitle: PropTypes.string,
};
